import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import styled from "styled-components";

import { LessonStandard } from "../../common/api";
import { ApiContext } from "../../common/context/ApiContext";
import { LoadingIndicator, PrimaryButton } from "../../common/CommonStyles";
import { COLOR_BLACK, COLOR_TEXT_SECONDARY } from "../../common/colors";
import {
  COMMON_CORE_STATE,
  getStoredStandardsState,
  pickInitialStandardsState,
  setStoredStandardsState,
  stateFromSubdivisionCode,
} from "../../common/standards";

// Academic standards this lesson is aligned to, chosen per US state from a
// dropdown of full state names ("Common Core State Standards" for the
// Multi-State rows), fetched lazily the first time the modal opens. The
// selected state is sticky across lessons via localStorage and preselected
// from the user's own state when the account carries one.

const StateSelectRow = styled.div`
  margin-bottom: 12px;
`;

const StandardRow = styled.div`
  margin-bottom: 12px;
`;

const StandardCode = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${COLOR_BLACK};
`;

const StandardDescription = styled.div`
  font-size: 14px;
  color: ${COLOR_TEXT_SECONDARY};
  /* Some standards are bullet lists; the server keeps their newlines. */
  white-space: pre-line;
`;

const CenteredBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
`;

const FeedbackForm = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeedbackControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Mirrored server-side; longer feedback is rejected with 400, not truncated.
const MAX_FEEDBACK_LENGTH = 4000;

type FeedbackStatus = "idle" | "submitting" | "sent" | "error";

// Dropdown states in display order: unique states from the response (server
// sorts by jurisdiction) with the Common Core pseudo-state pinned first.
// A user with a KNOWN state sees only their state + Common Core — the full
// state list is noise when we know which state they teach in; users
// without a state keep the full list.
const visibleStates = (
  standards: LessonStandard[],
  subdivisionCode: string | undefined
): string[] => {
  let states = Array.from(new Set(standards.map((standard) => standard.state)));
  const userState = stateFromSubdivisionCode(subdivisionCode);
  if (userState) {
    states = states.filter(
      (state) => state === COMMON_CORE_STATE || state === userState
    );
  }
  return [
    ...states.filter((state) => state === COMMON_CORE_STATE),
    ...states.filter((state) => state !== COMMON_CORE_STATE),
  ];
};

interface Props {
  lessonId: number;
  show: boolean;
  onHide: () => void;
  // The user's ISO 3166-2 code (e.g. "US-NJ") when known; drives preselection.
  subdivisionCode: string | undefined;
  // Beta-tester-only affordance, same gate as search feedback; the endpoint
  // accepts any authenticated user (UI rollout choice, not a security
  // boundary).
  canGiveFeedback: boolean;
}

const LessonStandardsModal = ({
  lessonId,
  show,
  onHide,
  subdivisionCode,
  canGiveFeedback,
}: Props) => {
  const { t } = useTranslation();
  const { getLessonStandards, submitLessonStandardsFeedback } =
    React.useContext(ApiContext);

  // null = never fetched; doubles as the cache so reopening never refetches.
  const [standards, setStandards] = React.useState<LessonStandard[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [selectedState, setSelectedState] = React.useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [feedbackStatus, setFeedbackStatus] =
    React.useState<FeedbackStatus>("idle");

  const submitFeedback = React.useCallback(() => {
    const feedback = feedbackText.trim();
    if (!feedback) {
      return;
    }
    setFeedbackStatus("submitting");
    submitLessonStandardsFeedback(lessonId, feedback).then((succeeded) => {
      if (succeeded) {
        setFeedbackText("");
        setFeedbackStatus("sent");
      } else {
        setFeedbackStatus("error");
      }
    });
  }, [feedbackText, lessonId, submitLessonStandardsFeedback]);

  const fetchStandards = React.useCallback(() => {
    setLoading(true);
    setError(false);
    getLessonStandards(lessonId)
      .then((response) => {
        setStandards(response.standards);
        setSelectedState(
          pickInitialStandardsState(
            visibleStates(response.standards, subdivisionCode),
            getStoredStandardsState(),
            subdivisionCode
          )
        );
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [getLessonStandards, lessonId, subdivisionCode]);

  // Fetch on the rising show edge only (errors are not cached — closing and
  // reopening retries too).
  const prevShow = React.useRef(show);
  React.useEffect(() => {
    if (!prevShow.current && show && standards === null && !loading) {
      fetchStandards();
    }
    prevShow.current = show;
  }, [show, standards, loading, fetchStandards]);

  const standardsByState = React.useMemo(() => {
    const byState = new Map<string, LessonStandard[]>();
    for (const standard of standards ?? []) {
      const rows = byState.get(standard.state) ?? [];
      rows.push(standard);
      byState.set(standard.state, rows);
    }
    return byState;
  }, [standards]);

  // Dropdown labels are the full jurisdiction names the server already
  // sends per row; only the Multi-State pseudo-state needs a friendlier
  // expansion than its dump name.
  const stateLabels = React.useMemo(() => {
    const labels = new Map<string, string>();
    for (const standard of standards ?? []) {
      labels.set(standard.state, standard.jurisdiction);
    }
    return labels;
  }, [standards]);

  const selectedRows =
    selectedState !== null ? standardsByState.get(selectedState) ?? [] : [];

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{t("standards_modal_title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <CenteredBody>
            <LoadingIndicator size="2x" />
          </CenteredBody>
        ) : error ? (
          <CenteredBody>
            <span>{t("standards_modal_error")}</span>
            <PrimaryButton onClick={fetchStandards}>
              {t("standards_modal_retry")}
            </PrimaryButton>
          </CenteredBody>
        ) : standards !== null && standards.length === 0 ? (
          <CenteredBody>
            <span>{t("standards_modal_empty")}</span>
          </CenteredBody>
        ) : (
          <>
            <StateSelectRow>
              <Form.Select
                value={selectedState ?? ""}
                aria-label={t("standards_modal_state_label")}
                onChange={(event) => {
                  setSelectedState(event.target.value);
                  setStoredStandardsState(event.target.value);
                }}
              >
                {visibleStates(standards ?? [], subdivisionCode).map((state) => (
                  <option key={state} value={state}>
                    {state === COMMON_CORE_STATE
                      ? t("standards_common_core_full")
                      : stateLabels.get(state) ?? state}
                  </option>
                ))}
              </Form.Select>
            </StateSelectRow>
            {selectedRows.map((standard) => (
              <StandardRow key={`${standard.state}-${standard.code}`}>
                <StandardCode>{standard.code}</StandardCode>
                <StandardDescription>{standard.description}</StandardDescription>
              </StandardRow>
            ))}
          </>
        )}
        {/* Deliberately independent of the standards fetch state: staff may
            want to report exactly a loading failure or an empty list. */}
        {canGiveFeedback && feedbackOpen && (
          <FeedbackForm>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={MAX_FEEDBACK_LENGTH}
              placeholder={t("standards_feedback_placeholder")}
              value={feedbackText}
              onChange={(event) => {
                setFeedbackText(event.target.value);
                // Typing again invalidates a stale sent/error message.
                if (feedbackStatus === "sent" || feedbackStatus === "error") {
                  setFeedbackStatus("idle");
                }
              }}
            />
            <FeedbackControls>
              <PrimaryButton
                onClick={submitFeedback}
                disabled={!feedbackText.trim() || feedbackStatus === "submitting"}
              >
                {t("generic_send")}
              </PrimaryButton>
              {feedbackStatus === "sent" && <span>{t("standards_feedback_sent")}</span>}
              {feedbackStatus === "error" && (
                <span>{t("standards_feedback_error")}</span>
              )}
            </FeedbackControls>
          </FeedbackForm>
        )}
      </Modal.Body>
      <Modal.Footer>
        {canGiveFeedback && (
          <Button
            variant="outline-secondary"
            onClick={() => setFeedbackOpen((open) => !open)}
          >
            {t("standards_feedback_button")}
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          {t("standards_modal_close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LessonStandardsModal;
